/** Presentation model for the reprice dialog (spec §2.5). Keeps ordering and
 *  quote-impact arithmetic out of JSX so both are testable.
 *
 *  A reprice diff covers every SKU the session prices — under an integration-%
 *  override that is the whole catalog — but only the SKUs the BOM carries are
 *  quote lines. Those get a quantity and an extended delta; the rest are priced
 *  but inert, and contribute nothing to the quote impact.
 *
 *  Moved lines sort first (largest absolute quote impact first) so the decision
 *  surface is at the top of a 37-row table; unchanged lines follow, by SKU. */
import type { RepriceDiff } from './repriceDiff';

export interface RepriceViewRow {
  productSku: string;
  oldPrice: number | null;
  newPrice: number | null;
  deltaPerUnit: number;
  /** BOM quantity, or null when the SKU is not a quote line. */
  qty: number | null;
  /** deltaPerUnit × qty, or null when there is no quantity or no new price. */
  deltaExtended: number | null;
  changed: boolean;
}
export interface RepriceView {
  rows: RepriceViewRow[];
  changedCount: number;
  totalCount: number;
  netQuoteImpact: number;
}

export function buildRepriceView(
  diff: RepriceDiff, qtyByPn: Record<string, number>,
): RepriceView {
  const rows: RepriceViewRow[] = diff.lines.map((l) => {
    const qty = qtyByPn[l.productSku] ?? null;
    // A dropped SKU (newPrice null) has an unknowable impact — null, never 0.
    const deltaExtended = qty !== null && l.newPrice !== null
      ? Math.round(l.deltaDollars * qty * 100) / 100 : null;
    return {
      productSku: l.productSku,
      oldPrice: l.oldPrice,
      newPrice: l.newPrice,
      deltaPerUnit: l.deltaDollars,
      qty,
      deltaExtended,
      changed: l.deltaDollars !== 0 || l.newPrice === null,
    };
  });

  rows.sort((a, b) => {
    if (a.changed !== b.changed) return a.changed ? -1 : 1;
    if (a.changed) {
      const impact = Math.abs(b.deltaExtended ?? 0) - Math.abs(a.deltaExtended ?? 0);
      if (impact !== 0) return impact;
    }
    return a.productSku.localeCompare(b.productSku);
  });

  const netQuoteImpact = Math.round(
    rows.reduce((sum, r) => sum + (r.deltaExtended ?? 0), 0) * 100) / 100;

  return {
    rows,
    changedCount: rows.filter((r) => r.changed).length,
    totalCount: rows.length,
    netQuoteImpact,
  };
}
